import BillableStatusModel    from 'Models/invoice/BillableStatusModel';
import BillingGroupModel      from 'Models/invoice/BillingGroupModel';
import InvoiceTypeModel       from 'Models/invoice/InvoiceTypeModel';
import SourceModel            from 'Models/invoice/SourceModel';
import doc                    from 'decorators/doc';
import model                  from 'decorators/model';
import nestedResolvable       from 'decorators/nestedResolvable';
import { Timestampable }      from 'helpers/traits';
import { Blamable }           from 'helpers/traits';
import { computed }           from 'mobx';
import InvoicePrivateApiModel from 'modelx/models/abstracts/InvoicePrivateApiModel';

@model.staticLabel('Facturable')
@model.urnResource('billable')
@doc.path('/billables/{?id}')
export default class BillableModel extends Blamable(Timestampable(InvoicePrivateApiModel)) {
	public _filters: ModelFiltersExtended<{
		'billableItems.billableItemMetas.reference': InvoiceBillableItemMetaReference;
		'billableItems.billableItemMetas.value': id;
		'billableItems.ownerLocation.locationUrn': Urn;
		'billableMetas': id;
		'billableMetas.reference': InvoiceBillableMetaReference;
		'billableMetas.value': id;
		'billableStatus': id;
		'billableStatus.reference': InvoiceBillableStatusReference;
		'billingGroup': id;
		'billingGroup.billingGroupType': id;
		'billingGroup.billingGroupType.reference': InvoiceBillingGroupTypeReference;
		'billingGroup.billingOwner.clientUrn': Urn;
		'billingGroup.billingOwner.companyUrn': Urn;
		'billingGroup.billingOwner.invoicingOwner.enterpriseGroupUrn': Urn;
		'billingGroup.billingOwner.invoicingOwner.enterpriseUrn': Urn;
		'billingGroup.billingOwner.ownerSubPartition.partitionUrn': Urn;
		'billingGroup.billingOwner.ownerSubPartition.subPartitionUrn': Urn;
		'billingGroup.invoicingGroup.invoicingOwner.enterpriseGroupUrn': Urn;
		'billingGroup.invoicingGroup.invoicingOwner.enterpriseUrn': Urn;
		'createdAt[after]': string;
		'createdAt[before]': string;
		'createdAt[strictly_after]': string;
		'createdAt[strictly_before]': string;
		'exists[sourceUrn]': '0' | '1';
		'invoiceType.reference': InvoiceInvoiceTypeReference;
		'invoiceableAt[after]': string;
		'invoiceableAt[before]': string;
		'invoiceableAt[strictly_after]': string;
		'invoiceableAt[strictly_before]': string;
		'refundedInvoices.invoiceRefunded': id;
		'sourceUrn': Urn;
		'updatedAt[after]': string | Date;
		'updatedAt[before]': string | Date;
		'updatedAt[strictly_after]': string | Date;
		'updatedAt[strictly_before]': string | Date;
	}> = {};

	public _sorts: ModelSortsExtended<{
		'billableStatus.position': string;
		'id': string;
		'updatedAt': string;
	}> = {};

	@doc.number declare amountExclTax: number;
	@doc.model(BillableStatusModel) declare billableStatus: BillableStatusModel;
	@doc.model(BillingGroupModel) declare billingGroup: BillingGroupModel;
	@doc.date declare date: Moment;
	@doc.string declare description: string;
	@doc.model(InvoiceTypeModel) declare invoiceType: InvoiceTypeModel;
	@doc.date declare invoiceableAt: Moment;
	@doc.string declare label: string;
	@doc.number declare position: number;
	@doc.boolean declare rebilling: boolean;
	@doc.modelUrn(SourceModel) declare source: SourceModel;

	@computed
	public get billableStatusId(): id {
		return this.get('billableStatus.id');
	}

	@computed
	public get billingGroupId(): id {
		return this.get('billingGroup.id');
	}

	@computed
	public get invoiceTypeId(): id {
		return this.get('invoiceType.id');
	}

	@nestedResolvable()
	public get partition() {
		return this.billingGroup.billingOwner.ownerSubPartition.partition;
	}

	@nestedResolvable()
	public get subPartition() {
		return this.billingGroup.billingOwner.ownerSubPartition.subPartition;
	}

	@nestedResolvable()
	public get enterprise() {
		return this.billingGroup.billingOwner.invoicingOwner.enterprise;
	}
}